/**
 * Le quattro sezioni di stadio di M4: Uscite · Confezionamento · Produzione ·
 * Materie prime.
 *
 * Il Cruscotto mostra **la giornata**; queste mostrano **il piano corrente** — chi
 * pesa, chi è lento, cosa è arretrato, cosa manca. Due finestre diverse, e ogni
 * testata dice quale, perché mescolarle è il modo più facile di far mentire i numeri.
 */

import { AlertTriangle } from "lucide-react";

import { RigaTesta } from "@/components/RigaTesta";
import { cn } from "@/lib/utils";
import { dec, kg, num, pct } from "@/lib/numeri";
import type {
  M4Arretrato,
  M4CaricoGiorno,
  M4ConfezionamentoResponse,
  M4LineaCarico,
  M4MateriePrimeResponse,
  M4ProduzioneResponse,
  M4UsciteResponse,
} from "@/types/api";

// ── mattoni condivisi ───────────────────────────────────────────────────────

function Blocco({
  titolo,
  nota,
  children,
}: {
  titolo: string;
  nota?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-2">
      <h2 className="text-[10.5px] font-semibold uppercase tracking-[0.085em] text-muted-foreground">
        {titolo}
        {nota && <span className="font-normal normal-case tracking-normal"> — {nota}</span>}
      </h2>
      {children}
    </section>
  );
}

function Quadro({
  valore,
  unita,
  nome,
  spiega,
  tono = "neutro",
}: {
  valore: string;
  unita?: string;
  nome: string;
  spiega: string;
  tono?: "neutro" | "attenzione" | "grave";
}) {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg border bg-card p-3">
      <p
        className={cn(
          "text-[22px] font-semibold leading-tight tabular-nums tracking-[-0.02em]",
          tono === "attenzione" && "text-[var(--m4-manca-ink)]",
          tono === "grave" && "text-destructive",
        )}
      >
        {valore}
        {unita && (
          <span className="ml-1 text-xs font-medium text-muted-foreground">{unita}</span>
        )}
      </p>
      <p className="text-xs font-semibold">{nome}</p>
      <p className="text-[10.5px] leading-snug text-muted-foreground">{spiega}</p>
    </div>
  );
}

function Quadri({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">{children}</div>;
}

function Lettura({
  children,
  attenzione,
}: {
  children: React.ReactNode;
  attenzione?: boolean;
}) {
  return (
    <p
      className={cn(
        "rounded-r-lg border border-l-[3px] bg-card px-3.5 py-2.5 text-xs leading-relaxed",
        attenzione ? "border-l-[var(--m4-manca)]" : "border-l-primary",
      )}
    >
      {children}
    </p>
  );
}

function Avviso({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-lg border border-[var(--m4-manca)]/35 bg-[var(--m4-manca)]/8 px-3.5 py-2.5 text-xs leading-relaxed text-[var(--m4-manca-ink)]">
      {children}
    </p>
  );
}

/** Barre orizzontali del carico per linea: la barra è la quota di chili. */
function BarreLinee({ linee, totaleKg }: { linee: M4LineaCarico[]; totaleKg: string }) {
  const massimo = Math.max(...linee.map((l) => parseFloat(l.kg)), 1);
  const totale = parseFloat(totaleKg) || 1;
  return (
    <div className="grid gap-2 rounded-xl border bg-card p-4">
      {linee.map((l) => (
        <div
          key={l.linea}
          className="grid grid-cols-[9rem_1fr_7rem] items-center gap-3"
        >
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold">{l.linea}</p>
            <p className="text-[10px] text-muted-foreground">
              {l.lavorazioni} lavorazioni · {l.senza_materiali} senza materiali
            </p>
          </div>
          <div className="h-[15px] overflow-hidden rounded bg-muted">
            <span
              className="block h-full rounded bg-[var(--m4-lavoro)]"
              style={{ width: `${(parseFloat(l.kg) / massimo) * 100}%` }}
            />
          </div>
          <div className="text-right">
            <p className="text-xs tabular-nums">{kg(l.kg)}</p>
            <p className="text-[10px] text-muted-foreground tabular-nums">
              {pct((parseFloat(l.kg) / totale) * 100, 1)} · {dec(l.ore)} ore
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Il carico giornata per giornata. I giorni **vuoti** ci sono: è così che si vede
 * la fermata, che il piano dichiara da sé.
 */
function CaricoGiorni({ giorni }: { giorni: M4CaricoGiorno[] }) {
  const massimo = Math.max(
    ...giorni.map((g) => g.estrusione + g.confezionamento + g.etichettatura),
    1,
  );
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex h-28 items-end gap-[3px]">
        {giorni.map((g) => {
          const totale = g.estrusione + g.confezionamento + g.etichettatura;
          return (
            <div
              key={g.giorno}
              title={`${g.giorno}: ${totale} lavorazioni — ${g.estrusione} estrusione, ${g.confezionamento} confezionamento, ${g.etichettatura} etichettatura`}
              className="flex min-w-0 flex-1 flex-col justify-end gap-px"
            >
              {[
                { v: g.etichettatura, c: "bg-[var(--m4-lavoro)]/35" },
                { v: g.confezionamento, c: "bg-[var(--m4-lavoro)]/70" },
                { v: g.estrusione, c: "bg-[var(--m4-lavoro)]" },
              ].map(
                ({ v, c }, i) =>
                  v > 0 && (
                    <span
                      key={i}
                      className={cn("rounded-t-sm", c)}
                      style={{ height: Math.max(2, (v / massimo) * 100) }}
                    />
                  ),
              )}
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex gap-[3px]">
        {giorni.map((g, i) => (
          <span
            key={g.giorno}
            className="flex-1 truncate text-center text-[8.5px] text-muted-foreground"
          >
            {i % 3 === 0 ? g.giorno.slice(8, 10) : ""}
          </span>
        ))}
      </div>
      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <i className="size-2.5 rounded-sm bg-[var(--m4-lavoro)]" />
          estrusione
        </span>
        <span className="flex items-center gap-1.5">
          <i className="size-2.5 rounded-sm bg-[var(--m4-lavoro)]/70" />
          confezionamento
        </span>
        <span className="flex items-center gap-1.5">
          <i className="size-2.5 rounded-sm bg-[var(--m4-lavoro)]/35" />
          etichettatura
        </span>
        <span>le colonne vuote sono giorni senza nessuna lavorazione in piano</span>
      </div>
    </div>
  );
}

function BloccoArretrato({ arretrato }: { arretrato: M4Arretrato }) {
  if (arretrato.lavorazioni === 0) {
    return (
      <Blocco titolo="Arretrato dichiarato dal MES">
        <Lettura>Nessuna lavorazione con data passata e stato «non avviata».</Lettura>
      </Blocco>
    );
  }
  return (
    <Blocco
      titolo="Arretrato dichiarato dal MES"
      nota="data di produzione passata e stato «non avviate» — non lo deduciamo noi"
    >
      <Quadri>
        <Quadro
          valore={num(arretrato.lavorazioni)}
          nome="Lavorazioni non avviate"
          spiega="con data di produzione già passata"
          tono="attenzione"
        />
        <Quadro
          valore={num(arretrato.giorni_attesa)}
          unita="giorni"
          nome="Attesa della più vecchia"
          spiega={`pianificata il ${arretrato.piu_vecchia ?? "—"}, non ancora avviata alla data della fotografia`}
          tono="attenzione"
        />
        <Quadro
          valore={num(arretrato.con_consegna_scaduta)}
          nome="Con consegna scaduta"
          spiega="la data promessa al cliente è già passata"
          tono="grave"
        />
        <Quadro
          valore={Object.entries(arretrato.per_fase)
            .map(([, n]) => n)
            .sort((a, b) => b - a)[0]
            ?.toString() ?? "—"}
          nome="Nella fase più colpita"
          spiega={
            Object.entries(arretrato.per_fase).sort((a, b) => b[1] - a[1])[0]?.[0].toLowerCase() ??
            "—"
          }
        />
      </Quadri>
    </Blocco>
  );
}

// ── Uscite ──────────────────────────────────────────────────────────────────

export function UsciteM4({ dati }: { dati: M4UsciteResponse }) {
  const d = dati.dove_la_merce;
  const ordinato = parseFloat(dati.ordinato) || 1;
  // ⚠️ Le classi sono **letterali**, non costruite concatenando stringhe: il JIT
  // di Tailwind legge il sorgente e non genera una classe che nel testo non
  // compare. Costruendola a runtime la fetta «pronto a magazzino» risultava
  // trasparente — cioè la fetta più importante della barra spariva.
  const fette = d
    ? ([
        {
          key: "ritirato",
          label: "ritirato",
          v: parseFloat(d.ritirato),
          classe: "bg-[var(--m4-lavoro)] text-white",
        },
        {
          key: "magazzino",
          label: "pronto a magazzino",
          v: parseFloat(d.magazzino),
          classe: "bg-[var(--m4-lavoro)]/75 text-white",
        },
        {
          key: "produzione",
          label: "in produzione",
          v: parseFloat(d.produzione),
          classe: "bg-[var(--m4-lavoro)]/45 text-white",
        },
        {
          key: "da_produrre",
          label: "da produrre",
          v: parseFloat(d.da_produrre),
          classe: "bg-[var(--m4-lavoro)]/20 text-foreground",
        },
      ] as const)
    : [];

  return (
    <div className="space-y-6">
      <RigaTesta
        contesto={`${num(dati.carichi_attivi)} carichi attivi`}
        caricatoIl={dati.snapshot_caricato_il}
        etichettaData="magazzino del"
        destra="Le spedizioni sono di ieri; il magazzino è del caricamento dichiarato qui"
      />

      <Quadri>
        <Quadro
          valore={num(dati.magazzino_pallet)}
          unita="pallet"
          nome="Merce pronta e non ritirata"
          spiega={`${num(d?.magazzino)} pezzi, circa ${dati.magazzino_camion} camion`}
          tono="attenzione"
        />
        <Quadro
          valore={num(dati.clienti_in_attesa)}
          nome="Clienti che aspettano una data"
          spiega={`lead time medio concordato: ${dec(dati.lead_time_medio_giorni)} giorni su ${dati.lead_time_campione} casi`}
        />
        <Quadro
          valore={num(dati.solleciti_dovuti)}
          nome="Avvisati che non rispondono"
          spiega="il sollecito è dovuto"
          tono="attenzione"
        />
        <Quadro
          valore={num(dati.giacenza.ferme)}
          unita="unità"
          nome="Magazzino che non si muove"
          spiega={`ferme da oltre 90 giorni (${dati.giacenza.ferme_oltre_180} da oltre 180) · ${dati.giacenza.scadute} già scadute`}
          tono="attenzione"
        />
      </Quadri>

      {d && (
        <Blocco
          titolo="Dov'è la merce"
          nota="i quattro posti in cui può stare, sommano all'ordinato"
        >
          <div className="rounded-xl border bg-card p-1">
            <div className="flex h-12 gap-0.5">
              {fette.map((f) => (
                <div
                  key={f.key}
                  title={`${f.label}: ${num(f.v)} pezzi`}
                  style={{ flex: f.v / ordinato }}
                  className={cn(
                    "flex min-w-0 flex-col justify-center overflow-hidden rounded px-2.5",
                    f.classe,
                  )}
                >
                  <span className="text-sm font-semibold tabular-nums leading-none">
                    {pct((f.v / ordinato) * 100, 1)}
                  </span>
                  <span className="truncate text-[10px] opacity-90">{f.label}</span>
                </div>
              ))}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {num(dati.ordinato)} pezzi ordinati in tutto. La fetta che conta è la seconda:
            merce già fatta che aspetta il cliente.
          </p>
        </Blocco>
      )}

      <Blocco
        titolo="Chi tiene ferma la merce"
        nota="quanto magazzino occupa ciascun cliente"
      >
        <div className="overflow-x-auto rounded-xl border bg-card">
          <table className="w-full min-w-[42rem] border-collapse text-xs">
            <thead>
              <tr className="border-b">
                {["Cliente", "Pronta e ferma", "Ordinato", "Già ritirato", "Commesse", "Quota ferma"].map(
                  (h, i) => (
                    <th
                      key={h}
                      className={cn(
                        "whitespace-nowrap px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground",
                        i === 0 ? "text-left" : "text-right",
                      )}
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {dati.clienti.map((c) => (
                <tr key={`${c.cliente_codice}-${c.cliente}`} className="border-b last:border-0">
                  <td className="px-3 py-2 font-semibold">{c.cliente}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">
                    {num(c.magazzino)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{num(c.ordinato)}</td>
                  <td
                    className={cn(
                      "px-3 py-2 text-right tabular-nums",
                      parseFloat(c.ritirato) === 0 && "text-destructive",
                    )}
                  >
                    {num(c.ritirato)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.commesse}</td>
                  <td className="px-3 py-2 text-right">
                    <span className="flex items-center justify-end gap-2">
                      <span className="h-1.5 w-12 overflow-hidden rounded-full bg-muted">
                        <span
                          className="block h-full bg-[var(--m4-lavoro)]"
                          style={{ width: `${c.quota_ferma_pct}%` }}
                        />
                      </span>
                      <b className="tabular-nums">{pct(c.quota_ferma_pct, 1)}</b>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-muted-foreground">
          {num(d?.magazzino)} pezzi fermi in tutto, su {dati.clienti_totali} clienti.
          Ordinati per quantità ferma, non per quota.
        </p>
      </Blocco>
    </div>
  );
}

// ── Confezionamento ─────────────────────────────────────────────────────────

export function ConfezionamentoM4({ dati }: { dati: M4ConfezionamentoResponse }) {
  const e = dati.etichettatura;
  const prima = dati.linee[0];
  const totale = parseFloat(dati.totale_kg) || 1;
  return (
    <div className="space-y-6">
      <RigaTesta
        contesto={`${dati.totale_lavorazioni} lavorazioni · ${dati.linee.length} linee`}
        caricatoIl={dati.piano_importato_il}
        etichettaData="piano del"
        destra="Prima volta che questo stadio viene misurato"
      />

      <Blocco titolo="Le confezionatrici" nota="quanto pesa ciascuna su tutto il piano">
        <BarreLinee linee={dati.linee} totaleKg={dati.totale_kg} />
        <p className="text-[11px] text-muted-foreground">
          {kg(dati.totale_kg)} in tutto · {dec(dati.totale_ore)} ore di linea
        </p>
        {prima && (
          <Lettura attenzione>
            <b>Una linea sola fa il {pct((parseFloat(prima.kg) / totale) * 100)} dei chili.</b>{" "}
            {prima.linea}: {kg(prima.kg)} su {kg(dati.totale_kg)}, in {dec(prima.ore)} ore. Se
            si ferma, si ferma quasi tutto il confezionamento.
          </Lettura>
        )}
      </Blocco>

      <Blocco
        titolo="Il carico giorno per giorno"
        nota="dalla data del piano in avanti; i giorni vuoti sono le fermate"
      >
        <CaricoGiorni giorni={dati.carico_per_giorno} />
      </Blocco>

      <Blocco titolo="L'etichettatura, che è lo stadio col buco">
        <Quadri>
          <Quadro
            valore={num(e.lavorazioni)}
            nome="Lavorazioni"
            spiega={e.linee.map((l) => l.linea).join(" · ") || "—"}
          />
          <Quadro
            valore={num(e.lavorazioni - e.con_durata)}
            nome="Senza durata dichiarata"
            spiega="il MES scrive «non è stato indicato il tempo della lavorazione»"
            tono="attenzione"
          />
          <Quadro
            valore={dec(e.ore_calcolabili)}
            unita="ore"
            nome="Ore calcolabili"
            spiega="solo sulle lavorazioni che hanno un tempo: è un minimo, non un totale"
          />
          <Quadro
            valore={num(e.arretrate)}
            nome="Arretrate"
            spiega="pianificate prima della data del piano e mai avviate"
            tono="attenzione"
          />
        </Quadri>
        <Avviso>
          <b>Perché questo stadio si legge a metà, e perché non è nelle tracce del Cruscotto.</b>{" "}
          Estrusione e confezionamento hanno la durata dichiarata su tutte le righe: le loro
          tracce sono vere. L'etichettatura ce l'ha su {e.con_durata} righe su {e.lavorazioni},
          quindi disegnarla accanto alle altre avrebbe mostrato linee mezze vuote che non sono
          vuote. Sta qui, dove si può spiegare.
        </Avviso>
      </Blocco>

      <BloccoArretrato arretrato={dati.arretrato} />
    </div>
  );
}

// ── Produzione ──────────────────────────────────────────────────────────────

export function ProduzioneM4({ dati }: { dati: M4ProduzioneResponse }) {
  const massimo = Math.max(
    ...dati.silos_settimane.flatMap((s) => [
      parseFloat(s.estruso_kg),
      parseFloat(s.confezionato_kg),
    ]),
    1,
  );
  const saldo = parseFloat(dati.saldo_silos_kg);
  const prima = dati.linee[0];
  const totale = parseFloat(dati.totale_kg) || 1;

  return (
    <div className="space-y-6">
      <RigaTesta
        contesto={`${dati.totale_lavorazioni} lavorazioni · ${dati.linee.length} estrusori · ${kg(dati.totale_kg)}`}
        caricatoIl={dati.piano_importato_il}
        etichettaData="piano del"
      />

      <Blocco titolo="Gli estrusori">
        <BarreLinee linee={dati.linee} totaleKg={dati.totale_kg} />
        <p className="text-[11px] text-muted-foreground">
          {kg(dati.totale_kg)} · {dec(dati.totale_ore)} ore di macchina
        </p>
        {prima && (
          <Lettura attenzione>
            <b>
              {prima.linea} fa il {pct((parseFloat(prima.kg) / totale) * 100)} dei chili
            </b>{" "}
            — {kg(prima.kg)} in {dec(prima.ore)} ore, cioè{" "}
            {num(parseFloat(prima.kg) / (prima.ore || 1))} kg l'ora. Le altre macchine si
            chiamano «estrusore» ma fanno lavori di scala diversa: la barra è la quota di
            chili, non di tempo.
          </Lettura>
        )}
        <Lettura>
          <b>Il costo dei cambi stampo non è qui.</b> Lo calcola la Pianificazione, e lo fa su
          un piano MES di data diversa: metterlo accanto a questi numeri li avrebbe fatti
          leggere come se fossero della stessa fotografia.
        </Lettura>
      </Blocco>

      <Blocco
        titolo="Il bilancio dei silos"
        nota="quanto entra nei silos contro quanto ne esce, settimana per settimana"
      >
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-end gap-6 overflow-x-auto pb-1">
            {dati.silos_settimane.map((s) => {
              const est = parseFloat(s.estruso_kg);
              const conf = parseFloat(s.confezionato_kg);
              const sal = parseFloat(s.saldo_kg);
              return (
                <div key={s.settimana_da} className="min-w-[7rem] flex-1">
                  <div className="flex h-32 items-end justify-center gap-1">
                    <span
                      title={`estruso ${kg(est)}`}
                      className="w-8 rounded-t bg-[var(--m4-lavoro)]"
                      style={{ height: `${Math.max(2, (est / massimo) * 100)}%` }}
                    />
                    <span
                      title={`confezionato ${kg(conf)}`}
                      className="w-8 rounded-t bg-[var(--m4-lavoro)]/45"
                      style={{ height: `${Math.max(2, (conf / massimo) * 100)}%` }}
                    />
                  </div>
                  <p className="mt-1.5 text-center text-[11px]">
                    sett. {s.settimana_da.slice(8, 10)}/{s.settimana_da.slice(5, 7)}
                  </p>
                  <p
                    className={cn(
                      "text-center text-[10.5px] font-semibold tabular-nums",
                      sal < 0 ? "text-destructive" : "text-emerald-700",
                    )}
                  >
                    {sal < 0 ? "−" : "+"}
                    {kg(Math.abs(sal))}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <i className="size-2.5 rounded-sm bg-[var(--m4-lavoro)]" />
              estruso (entra nei silos)
            </span>
            <span className="flex items-center gap-1.5">
              <i className="size-2.5 rounded-sm bg-[var(--m4-lavoro)]/45" />
              confezionato (esce dai silos)
            </span>
            <span>le settimane senza lavorazioni non compaiono: sono le fermate</span>
          </div>
        </div>
        <Avviso>
          <b>Il dato che manca, e che serve solo questo.</b> Nell'orizzonte del piano il
          confezionamento consuma <b>{kg(Math.abs(saldo))}</b> di semilavorato{" "}
          {saldo < 0 ? "più" : "meno"} di quanti l'estrusione ne produca. Se nei silos quella
          scorta c'è, è tutto normale; se non c'è, le confezionatrici si fermano.{" "}
          <b>La scorta nei silos non è in nessun dato che abbiamo</b> — e non si può nemmeno
          fare il conto silo per silo, perché il campo che lega l'estruso al confezionato è
          vuoto su tutte le righe del piano.
        </Avviso>
      </Blocco>

      <BloccoArretrato arretrato={dati.arretrato} />
    </div>
  );
}

// ── Materie prime ───────────────────────────────────────────────────────────

export function MateriePrimeM4({ dati }: { dati: M4MateriePrimeResponse }) {
  const quota = dati.lavorazioni > 0 ? (dati.senza_materiali / dati.lavorazioni) * 100 : null;
  const massimo = Math.max(...dati.per_fase.map((f) => f.lavorazioni), 1);
  const confermateSenza = dati.incrocio_stato.find(
    (r) => r.stato === "Confermato" && r.materiali_disponibili === false,
  );
  const confermateCon = dati.incrocio_stato.find(
    (r) => r.stato === "Confermato" && r.materiali_disponibili === true,
  );
  const nonConfermateSenza = dati.incrocio_stato.find(
    (r) => r.stato === "Non confermato" && r.materiali_disponibili === false,
  );
  const nonConfermateTot = dati.incrocio_stato
    .filter((r) => r.stato === "Non confermato")
    .reduce((a, r) => a + r.lavorazioni, 0);

  return (
    <div className="space-y-6">
      <RigaTesta
        contesto="una domanda sola: le materie prime stanno bloccando il piano?"
        caricatoIl={dati.piano_importato_il}
        etichettaData="piano del"
      >
        <span className="rounded-full border border-[var(--m4-manca)]/40 bg-[var(--m4-manca)]/10 px-2 py-px text-[10px] font-semibold uppercase tracking-[0.04em] text-[var(--m4-manca-ink)]">
          parziale
        </span>
      </RigaTesta>

      <Avviso>
        <b>Cosa c'è e cosa manca, dichiarato in testa.</b> Non abbiamo l'inventario delle
        materie prime: nei nostri magazzini c'è <b>solo prodotto finito confezionato</b>, zero
        materia prima e zero semilavorato. Le distinte base stanno in Cube. Quello che abbiamo
        è <b>il giudizio del MES riga per riga</b>: per ogni lavorazione pianificata dice se i
        materiali sono disponibili. Non dice quanto c'è di cosa, dice <b>l'effetto sul piano</b>{" "}
        — che per un cruscotto di controllo è la parte che serve.
      </Avviso>

      <Blocco titolo="Quante lavorazioni il MES dichiara senza materiali">
        <div className="grid gap-2 rounded-xl border bg-card p-4">
          {dati.per_fase.map((f) => (
            <div key={f.fase} className="grid grid-cols-[11rem_1fr_6rem] items-center gap-3">
              <div className="min-w-0">
                <p className="truncate text-xs font-semibold capitalize">
                  {f.fase.toLowerCase()}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {f.lavorazioni} lavorazioni nel piano
                </p>
              </div>
              <div className="h-[15px] overflow-hidden rounded bg-muted">
                <span
                  className="block h-full rounded bg-[var(--m4-manca)]"
                  style={{ width: `${(f.senza_materiali / massimo) * 100}%` }}
                />
              </div>
              <div className="text-right">
                <p className="text-xs tabular-nums">
                  {f.senza_materiali} su {f.lavorazioni}
                </p>
                <p className="text-[10px] text-muted-foreground tabular-nums">
                  {pct((f.senza_materiali / (f.lavorazioni || 1)) * 100)}
                </p>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground">
          {dati.senza_materiali} lavorazioni su {dati.lavorazioni} in tutto il piano corrente
          {quota !== null && ` (${pct(quota)})`}
        </p>
      </Blocco>

      <Blocco titolo="Perché non è scritto come un allarme">
        <Lettura attenzione>
          <AlertTriangle className="mr-1.5 inline size-3.5" />
          <b>
            Il {quota !== null ? pct(quota) : "—"} non si può leggere come «manca il{" "}
            {quota !== null ? pct(quota) : "—"} delle materie prime».
          </b>{" "}
          Il flag coincide quasi esattamente con lo stato della riga: fra le lavorazioni marcate
          «Non confermato» ne risultano senza materiali{" "}
          <b>
            {nonConfermateSenza?.lavorazioni ?? 0} su {nonConfermateTot}
          </b>
          ; fra le «Confermato»,{" "}
          <b>
            {confermateSenza?.lavorazioni ?? 0} su{" "}
            {(confermateSenza?.lavorazioni ?? 0) + (confermateCon?.lavorazioni ?? 0)}
          </b>
          . Una coincidenza così stretta non è un caso.
        </Lettura>
        <Lettura attenzione>
          <b>E i due campi non dicono quello che sembra.</b> Incrociando tipologia e stato:{" "}
          {dati.incrocio_tipologia
            .slice(0, 3)
            .map((r) => `${r.tipologia}/${r.stato} ${r.lavorazioni}`)
            .join(" · ")}
          . Finché non sappiamo cosa significano quei due campi nel MES, il numero è{" "}
          <b>un segnale, non un fatto</b>.
        </Lettura>
        <Lettura>
          <b>Serve una domanda sola a chi pianifica</b> — cosa vuol dire «materiali
          disponibili» in quell'export, e cosa distingue «Ordine» da «Proposta» — e questa
          sezione passa da segnale a misura, senza scrivere una riga di codice in più.
        </Lettura>
      </Blocco>
    </div>
  );
}
